import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dumbbell, ArrowRight, Sun, Moon } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";

/* ---------------- Top nav (shared across landing + legal pages) ---------------- */
export function SiteNav() {
  const { theme, toggle } = useTheme();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${
        scrolled ? "bg-background/85 backdrop-blur-md border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between px-6 lg:px-12 h-16">
        <Link to="/" className="flex items-center" data-testid="site-brand">
          <img src="/logo-light.png" alt="Fitnudge" className="h-6 w-auto dark:hidden" draggable={false} />
          <img src="/logo-dark.png" alt="Fitnudge" className="h-6 w-auto hidden dark:block" draggable={false} />
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
          <Link to="/#problem" className="hover:text-foreground transition-colors">Problem</Link>
          <Link to="/#how" className="hover:text-foreground transition-colors">How it works</Link>
          <Link to="/#benefits" className="hover:text-foreground transition-colors">Benefits</Link>
          <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="site-theme-toggle"
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          {user ? (
            <Link
              to="/dashboard"
              data-testid="site-nav-dashboard"
              className="text-sm font-medium px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
            >
              Open dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="site-nav-login"
                className="hidden sm:inline-block text-sm font-medium px-3 py-2 text-foreground/80 hover:text-foreground transition-colors"
              >
                Login
              </Link>
              <Link
                to="/login"
                data-testid="site-nav-cta"
                className="text-sm font-medium px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors flex items-center gap-1.5"
              >
                Get started <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

/* ---------------- Footer (shared) ---------------- */
const FOOTER_LINKS = [
  { to: "/about", label: "About Us", testid: "footer-about" },
  { to: "/contact", label: "Contact Us", testid: "footer-contact" },
  { to: "/privacy", label: "Privacy Policy", testid: "footer-privacy" },
  { to: "/terms", label: "Terms & Conditions", testid: "footer-terms" },
  { to: "/refund-policy", label: "Refund Policy", testid: "footer-refund" },
];

export function SiteFooter() {
  return (
    <footer className="border-t border-border px-6 lg:px-12 py-12" data-testid="site-footer">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
          <div className="flex items-center gap-3">
            <img src="/logo-light.png" alt="Fitnudge" className="h-5 w-auto dark:hidden" draggable={false} />
            <img src="/logo-dark.png" alt="Fitnudge" className="h-5 w-auto hidden dark:block" draggable={false} />
            <span className="text-xs text-muted-foreground">fitnudge.in</span>
          </div>

          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground" aria-label="Legal & company">
            {FOOTER_LINKS.map(l => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className="hover:text-foreground transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="mt-8 pt-6 border-t border-border/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} Fitnudge · Built for gyms in India</p>
          <p>
            Questions?{" "}
            <a href="mailto:support@fitnudge.in" className="hover:text-foreground transition-colors">
              support@fitnudge.in
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

/* ---------------- Legal-page reading container ---------------- */
/**
 * Shared layout for /privacy, /terms, /refund-policy, /contact, /about.
 * Provides nav + footer + a max-width reading container matching the
 * existing landing-page design system.
 */
export function LegalPage({ title, subtitle, lastUpdated, children, testid }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid={testid}>
      <SiteNav />
      <main className="flex-1 px-6 lg:px-12 pt-32 pb-20">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary mb-3">
            Fitnudge
          </p>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
              {subtitle}
            </p>
          )}
          {lastUpdated && (
            <p className="mt-5 text-xs uppercase tracking-wider text-muted-foreground/80">
              Last updated · {lastUpdated}
            </p>
          )}
          <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-foreground/85">
            {children}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

/* ---------------- Section helpers for legal-page content ---------------- */
export function LegalSection({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight text-foreground mb-3">
        {title}
      </h2>
      <div className="space-y-3 text-foreground/80">{children}</div>
    </section>
  );
}

export function LegalList({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 text-foreground/80 marker:text-primary/60">
      {items.map((i, idx) => <li key={idx}>{i}</li>)}
    </ul>
  );
}
